// Fan-out and the local draw order (issue 1030, TOKEN-019). Pure: the board hands in the footprints of what THIS
// viewer was given and gets back display offsets — stored positions never change, and a token a player was not sent
// (a hidden one) cannot be in their stack because it is not in their store.

export interface FanRect { x: number; y: number; width: number; height: number }
export interface FanThing { id: string; layer: string; rect: FanRect }
export interface FanOffset { dx: number; dy: number }

const centreIn = (a: FanRect, b: FanRect): boolean => {
  const cx = a.x + a.width / 2; const cy = a.y + a.height / 2; const e = 1e-6;
  return cx > b.x + e && cx < b.x + b.width - e && cy > b.y + e && cy < b.y + b.height - e;
};

/**
 * Things on one layer whose centres fall inside each other's footprints are a stack; each stack is spread along the
 * diagonal, biggest at the back-left, smallest at the front-right, about the stack's own centre. A thing alone has
 * no entry. The spread is at most 60% of the smallest thing's side, so a stack never wanders far off its square.
 */
export function fanOut(things: FanThing[]): Map<string, FanOffset> {
  const parent = new Map<string, string>(things.map((t) => [t.id, t.id]));
  const root = (id: string): string => { let r = id; while (parent.get(r) !== r) r = parent.get(r)!; parent.set(id, r); return r; };
  for (let i = 0; i < things.length; i++) {
    for (let j = i + 1; j < things.length; j++) {
      const a = things[i]!; const b = things[j]!;
      if (a.layer !== b.layer) continue;
      if (centreIn(a.rect, b.rect) || centreIn(b.rect, a.rect)) parent.set(root(a.id), root(b.id));
    }
  }
  const stacks = new Map<string, FanThing[]>();
  for (const t of things) { const r = root(t.id); stacks.set(r, [...(stacks.get(r) ?? []), t]); }
  const out = new Map<string, FanOffset>();
  for (const stack of stacks.values()) {
    if (stack.length < 2) continue;
    // Biggest first (at the back), then by id: the same answer whatever order the store listed them in.
    stack.sort((a, b) => b.rect.width * b.rect.height - a.rect.width * a.rect.height || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const side = Math.min(...stack.map((t) => Math.min(t.rect.width, t.rect.height)));
    const step = Math.min(side * 0.3, (side * 0.6) / (stack.length - 1));
    stack.forEach((t, k) => { const d = (k - (stack.length - 1) / 2) * step; out.set(t.id, { dx: d, dy: d }); });
  }
  return out;
}

const LAYER_RANK: Record<string, number> = { scenery: 0, token: 1, dm: 2 };

/**
 * The draw order: by layer; within a layer the one clicked last on top, then smaller over bigger, then as listed (`raised` holds a click counter per id).
 * This viewer's alone — nothing is sent or stored — which is all "both stay reachable" needs: a click raises the one
 * clicked, on this screen, without a permission question (a player may raise the DM's goblin to read it).
 */
export function raiseOrder<T extends { id: string; size: { w: number; h: number } }>(things: T[], raised: Map<string, number>, layerOf: (t: T) => string = (t) => (t as unknown as { layer: string }).layer): T[] {
  const area = (t: T) => t.size.w * t.size.h;
  // Unclicked, a bigger thing draws under a smaller one: a 2×2 ogre added after a 1×1 hero would cover it whole.
  return things
    .map((t, k) => ({ t, k }))
    .sort((a, b) => (LAYER_RANK[layerOf(a.t)] ?? 1) - (LAYER_RANK[layerOf(b.t)] ?? 1) || (raised.get(a.t.id) ?? 0) - (raised.get(b.t.id) ?? 0)
      || area(b.t) - area(a.t) || a.k - b.k)
    .map(({ t }) => t);
}
